import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

export const getSpotItemNameKey = (spotItemName) => `${spotItemName || ''}`.trim().toLowerCase();

// A merged line stays unpriced until every source line has a cost, so the mandatory-cost check catches it.
export const getTotalCostIfAllPriced = (cost, otherCost) =>
	cost !== undefined && otherCost !== undefined ? cost + otherCost : undefined;

const toPOLineItem = (prLineItem, prefix) => {
	const cost = prLineItem[`${prefix}Cost`];
	const quantity = prLineItem[`${prefix}Quantity`];
	return {
		[`${prefix}Cost`]: cost !== undefined ? parseFloat(cost) : undefined,
		[`${prefix}Quantity`]: quantity,
		unitCost: cost !== undefined ? cost / quantity : undefined,
		prLineItemIds: [prLineItem.id],
		warrantyAssetId: nullSafeGet('supplierPurchaseRequest.warrantyAssetId', prLineItem),
		warrantyAsset: nullSafeGet('supplierPurchaseRequest.warrantyAsset', prLineItem),
		supplierPurchaseRequestLineItems: [prLineItem],
		glCode: nullSafeGet('glCodeForPurchaseOrderLineItem', prLineItem),
	};
};

// Merges line items sharing a key into one PO line, on top of the existing records.
// Null-prototype accumulator: spot keys are free text, so `constructor` must not hit Object.prototype.
const mergeLineItems = (lineItems, prefix, getKey, existingRecords) => {
	const costField = `${prefix}Cost`;
	const quantityField = `${prefix}Quantity`;
	const recordsByKey = lineItems.reduce((acc, el) => {
		const key = getKey(el);
		const combinedEl = acc[key];
		if (combinedEl) {
			const quantity = el[quantityField] + combinedEl[quantityField];
			const cost = getTotalCostIfAllPriced(combinedEl[costField], el[costField]);
			acc[key] = {
				...combinedEl,
				...(el.warrantyAssetId && { warrantyAssetId: el.warrantyAssetId }),
				...(el.warrantyAsset && { warrantyAsset: el.warrantyAsset }),
				[quantityField]: quantity,
				unitCost: cost !== undefined ? cost / quantity : undefined,
				[costField]: cost,
				prLineItemIds: nullSafeGetOrElse('prLineItemIds', combinedEl, []).concat(el.prLineItemIds),
				supplierPurchaseRequestLineItems: combinedEl.supplierPurchaseRequestLineItems.concat(
					el.supplierPurchaseRequestLineItems
				),
			};
		} else {
			acc[key] = el;
		}
		return acc;
	}, Object.assign(Object.create(null), existingRecords));
	return getObjectValues(recordsByKey);
};

export const getPOLineItemsFromSelectedRecords = (
	selectedRecords,
	partRecords = {},
	equipmentRecords = {},
	spotItemRecords = {}
) => {
	const partLineItems = selectedRecords
		.filter((_) => !_.isEquipmentLine && !_.isSpotItemLine)
		.map((requestedPart) => ({
			...toPOLineItem(requestedPart, 'part'),
			partCatalog: requestedPart.partCatalog,
			partId: requestedPart.partId,
			part: requestedPart.part,
		}));

	const equipmentLineItems = selectedRecords
		.filter((_) => _.isEquipmentLine)
		.map((requestedEquipment) => ({
			...toPOLineItem(requestedEquipment, 'equipment'),
			equipmentCatalog: requestedEquipment.equipmentCatalog,
			equipmentTypeId: requestedEquipment.equipmentTypeId,
			equipmentType: requestedEquipment.equipmentType,
		}));

	const spotItemLineItems = selectedRecords
		.filter((_) => _.isSpotItemLine)
		.map((requestedSpotItem) => ({
			...toPOLineItem(requestedSpotItem, 'spotItem'),
			isSpotItemLine: true,
			spotItemName: requestedSpotItem.spotItemName,
			spotItemCurrencyId: requestedSpotItem.spotItemCurrencyId,
		}));

	return {
		partLineItems: mergeLineItems(partLineItems, 'part', (_) => _.partId, partRecords),
		equipmentLineItems: mergeLineItems(
			equipmentLineItems,
			'equipment',
			(_) => _.equipmentTypeId,
			equipmentRecords
		),
		spotItemLineItems: mergeLineItems(
			spotItemLineItems,
			'spotItem',
			(_) => getSpotItemNameKey(_.spotItemName),
			spotItemRecords
		),
	};
};
